import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { isServer } from '@app/utils';
import { httpsAgent, httpAgent } from '@app/server.http';
import attachRequestLogger from './interceptors/logger.server';
import attachCache from './interceptors/cache.server';

export type SlimAxiosRequestConfig = Omit<
  AxiosRequestConfig,
  'httpAgent' | 'httpsAgent'
>;

export type { AxiosInstance } from 'axios';

type createAxiosInstanceProps = {
  axiosOptions: SlimAxiosRequestConfig;
  interceptor?: string;
  enableLogging?: boolean;
  enableCaching?: boolean;
};

/**
 * Creates an Axios instance that has keep alive agents baked in.
 * The defaults are:
 *
 *   http timeout: 30 seconds
 *   free socket timeout: 3 seconds
 *
 * Both of these can be overwritten.
 *
 * @param config Configuration for all clients created
 */
const createAxiosInstance = ({
  axiosOptions = {},
  enableCaching = false,
  enableLogging = false,
}: createAxiosInstanceProps): AxiosInstance => {
  const instanceConfig = {
    timeout: 30 * 1000,
    httpAgent: isServer ? httpAgent : undefined,
    httpsAgent: isServer ? httpsAgent : undefined,
    ...axiosOptions,
  };

  const instance = axios.create(instanceConfig);

  if (!isServer) {
    return instance;
  }

  if (enableCaching && attachCache) {
    attachCache(instance);
  }

  if (enableLogging && attachRequestLogger) {
    attachRequestLogger(instance);
  }

  return instance;
};

export default createAxiosInstance;
